import { memo, useState, useRef, useMemo, useEffect, useCallback, Fragment } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import { trackClientEvent } from 'analytics/analytics'
import MaldivesDirectSearchFlightCalendarItem from './MaldivesDirectSearchFlightCalendarItem'
import MaldivesOfferCalendarItem from './MaldivesOfferCalendarItem'
import { CHARTER_CALENDAR_PAGE_SIZE } from 'constants/charterFlight'
import type { FlightsClassTypes } from 'constants/flight'
import type { CalendarItemBundleData } from '../hooks/types'
import Hr from 'components/Common/Hr'

interface DateRange {
  departureDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  durationDays: number // Total days from departureDate to returnDate
  nights: number // Nights from checkIn to checkOut
}

interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  month?: string
}

export interface DateItem {
  id: string
  checkIn: string
  checkOut: string
  departDate: string
  returnDate: string
  month: string
}

interface Props {
  currentMonthTripOptions: Array<DateItem>
  selectedMonth: string
  dateRangesByMonth: Record<string, Array<DateRange>>
  luxPlusEnabled: boolean
  selectedOptionId?: string
  onOptionSelect: (option: TripOption) => void
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  mode: 'search' | 'offer'
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  /** When mode is 'offer', pass the offer for Kids Fly Free label visibility */
  offer?: App.HotelOffer
}

const OptionsGrid = styled.div`
  display: grid;
  align-items: center;
  gap: ${rem(20)} ${rem(12)};
  grid-template-columns: auto auto auto 1fr auto;
`

const DepartureHeader = styled(BodyText)`
  grid-column: 1;
`

const ReturnHeader = styled(BodyText)`
  grid-column: 3;
`

const PriceHeader = styled(BodyText)`
  grid-column: 5;
`

const RowDivider = styled(Hr)`
  grid-column: 1 / -1;
`

const EmptyStateGroup = styled(Group)`
  padding: ${rem(32)};
`

const ShowMoreButtonContainer = styled.div`
  padding: ${rem(20)};
  display: flex;
  justify-content: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  grid-column: 1 / -1;
`

const pageSize = CHARTER_CALENDAR_PAGE_SIZE

const TripOptionsList = memo(function TripOptionsList(props: Props) {
  const {
    currentMonthTripOptions,
    selectedMonth,
    dateRangesByMonth,
    luxPlusEnabled,
    selectedOptionId,
    onOptionSelect,
    nights,
    rooms,
    fareClass,
    priceMode,
    mode,
    itemBundleDataByDate,
    offer,
  } = props

  const intl = useIntl()
  const [page, setPage] = useState<number>(1)
  const lastTrackedEmptyStateMonthRef = useRef<string | undefined>(undefined)

  // Get the first date of the selected month to determine the year
  const selectedMonthDateRanges = dateRangesByMonth[selectedMonth]
  const selectedMonthFirstDate = selectedMonthDateRanges?.[0]?.checkIn
  const selectedMonthMoment = selectedMonthFirstDate
    ? moment(selectedMonthFirstDate).startOf('month')
    : null

  // Filter visible items and calculate limit
  const visibleItems = useMemo(() => {
    return currentMonthTripOptions.filter((item) => {
      if (!selectedMonthMoment) return false
      const itemMonthMoment = moment(item.checkIn).startOf('month')
      return itemMonthMoment.isSameOrAfter(selectedMonthMoment, 'month')
    })
  }, [currentMonthTripOptions, selectedMonthMoment])

  const limit = page * pageSize
  const itemsToShow = visibleItems.slice(0, limit)
  const hasMore = visibleItems.length > limit

  // Reset page when selectedMonth changes
  useEffect(() => {
    setPage(1)
  }, [selectedMonth])

  const onShowMore = useCallback(() => {
    setPage(prevPage => prevPage + 1)
  }, [])

  const showMoreDatesText = intl.formatMessage({
    id: 'LIfL6oXSbJk-SgAW57Pm',
    defaultMessage: 'Show more dates',
    description: 'Button text to load more departure dates in Maldives charter calendar',
  })

  const departsMelbourneText = intl.formatMessage({
    id: 'KYNHMJQlr4RmsQjArKgd',
    defaultMessage: 'Departs Melbourne',
    description: 'Column header for Melbourne departure dates in charter flight calendar',
  })

  const departsMaleText = intl.formatMessage({
    id: '1eF7GGkqghJ1PBcYhIyX',
    defaultMessage: 'Departs Malé',
    description: 'Column header for Malé departure dates in charter flight calendar',
  })

  const flightHotelPriceText = intl.formatMessage({
    id: 'dAhViGtKuotbrFmUK8UK',
    defaultMessage: 'Flight + Hotel price',
    description: 'Column header for combined flight and hotel price in charter calendar',
  })

  const emptyStateBodyText = intl.formatMessage({
    id: '2y3hsx4QLvrR_aamxC4j',
    defaultMessage: 'Try selecting a different month or adjusting your search criteria',
    description: 'Help text when no charter departures are available for selected month',
  })

  const isEmptyState = currentMonthTripOptions.length === 0

  useEffect(() => {
    if (!isEmptyState || mode !== 'search') {
      lastTrackedEmptyStateMonthRef.current = undefined
      return
    }

    if (lastTrackedEmptyStateMonthRef.current === selectedMonth) {
      return
    }

    lastTrackedEmptyStateMonthRef.current = selectedMonth
    trackClientEvent({
      subject: 'no_departures_available_selected_month',
      action: 'impression',
      type: 'nonInteraction',
      category: 'maldives_direct_calendar',
    })
  }, [isEmptyState, mode, selectedMonth])

  // Early return after all hooks
  if (isEmptyState) {
    return (
      <EmptyStateGroup direction="vertical" horizontalAlign="center" gap={8}>
        <Heading variant="heading5" align="center">
          {intl.formatMessage(
            {
              id: 'guyDFMVuGdGAJHuZXY-E',
              defaultMessage: 'No departures available for {selectedMonth}',
              description: 'Empty state heading when no charter departures found for selected month',
            },
            { selectedMonth },
          )}
        </Heading>
        <BodyText variant="medium" align="center" colour="neutral-three">
          {emptyStateBodyText}
        </BodyText>
      </EmptyStateGroup>
    )
  }

  return (
    <OptionsGrid>
      <DepartureHeader variant="small" colour="neutral-three">{departsMelbourneText}</DepartureHeader>
      <ReturnHeader variant="small" colour="neutral-three">{departsMaleText}</ReturnHeader>
      <PriceHeader variant="small" colour="neutral-three">{flightHotelPriceText}</PriceHeader>

      <RowDivider />

      {itemsToShow.map((item) => {
        const isSelected = item.id === selectedOptionId
        const commonProps = {
          checkIn: item.checkIn,
          checkOut: item.checkOut,
          departDate: item.departDate,
          returnDate: item.returnDate,
          nights: nights,
          priceMode: priceMode,
          luxPlusEnabled: luxPlusEnabled,
          isSelected: isSelected,
          onSelect: onOptionSelect,
        }

        switch (mode) {
          case 'search':
            return (
              <Fragment key={item.id}>
                <MaldivesDirectSearchFlightCalendarItem
                  {...commonProps}
                  rooms={rooms}
                  fareClass={fareClass}
                  prefetchedBundleData={itemBundleDataByDate?.[item.checkIn]}
                />
                <RowDivider />
              </Fragment>
            )
          case 'offer': {
            // Get pre-calculated bundle data for this date
            const itemBundleData = itemBundleDataByDate?.[item.checkIn]

            // Fallback to default empty state if data is missing
            const itemData: CalendarItemBundleData = itemBundleData ?? {
              fetching: true,
              available: false,
              totalPrice: 0,
              pricePerPerson: 0,
              offerCount: 0,
              seatsLeft: 0,
              hasNoPackage: false,
            }

            return (
              <Fragment key={item.id}>
                <MaldivesOfferCalendarItem
                  {...commonProps}
                  bundleData={itemData}
                  rooms={rooms}
                  fareClass={fareClass}
                  offer={offer}
                />
                <RowDivider />
              </Fragment>
            )
          }
          default:
            return null
        }
      })}
      {hasMore && <ShowMoreButtonContainer>
        <TextButton
          variant="dark"
          kind="secondary"
          onClick={onShowMore}
        >
          {showMoreDatesText}
        </TextButton>
      </ShowMoreButtonContainer>}
    </OptionsGrid>
  )
})

export default TripOptionsList
